import React, { useState, useEffect } from 'react';
import { Layout } from '@/components/Layout';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Droplets, 
  DollarSign, 
  TrendingUp, 
  AlertCircle,
  Gauge,
  Activity,
  Zap,
  Clock,
  Award,
  X,
  ChevronRight,
  Bell
} from 'lucide-react';
import { 
  PipeFlowAnimation,
  AnimatedCounter, 
  PulsingDot,
  LeakAlertAnimation,
  GaugeAnimation 
} from '@/components/AnimatedComponents';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const API = `${BACKEND_URL}/api`;

const CustomerDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [tips, setTips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadAlerts, setUnreadAlerts] = useState(0);

  useEffect(() => {
    fetchDashboardData();
    fetchAlerts();
    fetchWaterSavingTips();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/dashboard/customer`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAlerts = async () => {
    try {
      const token = localStorage.getItem('token');
      const [alertsRes, unreadRes] = await Promise.all([
        axios.get(`${API}/alerts?limit=5`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/alerts/unread-count`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setAlerts(alertsRes.data || []);
      setUnreadAlerts(unreadRes.data?.unread_count || 0);
    } catch (error) {
      console.error('Failed to fetch alerts:', error);
    }
  };

  const fetchWaterSavingTips = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/alerts/tips?viewed=false&limit=3`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTips(response.data || []);
    } catch (error) {
      console.error('Failed to fetch tips:', error);
    }
  };

  const dismissAlert = async (alertId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API}/alerts/${alertId}/status`,
        { status: 'dismissed' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAlerts(alerts.filter(a => a.id !== alertId));
      setUnreadAlerts(Math.max(0, unreadAlerts - 1));
    } catch (error) {
      console.error('Failed to dismiss alert:', error);
    }
  };

  const markTipViewed = async (tipId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API}/alerts/tips/${tipId}/viewed`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTips(tips.filter(t => t.id !== tipId));
    } catch (error) {
      console.error('Failed to mark tip as viewed:', error);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(value || 0);
  };

  const getAlertIcon = (type) => {
    switch (type) {
      case 'leak_detected':
        return <LeakAlertAnimation />;
      case 'low_balance':
        return <DollarSign className="w-6 h-6 text-yellow-600" />;
      case 'device_tampering':
        return <AlertCircle className="w-6 h-6 text-red-600" />;
      default:
        return <Bell className="w-6 h-6 text-blue-600" />;
    }
  };

  const getAlertColor = (severity) => {
    switch (severity) {
      case 'critical':
        return 'bg-red-50 border-red-200';
      case 'warning':
        return 'bg-yellow-50 border-yellow-200';
      default:
        return 'bg-blue-50 border-blue-200';
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <motion.div 
            className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full"
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600 mt-1">Welcome back, {user?.full_name}</p>
        </motion.div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-shadow"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Balance</p>
                <p className="text-2xl font-bold text-gray-900 mt-2">
                  {formatCurrency(stats?.metrics?.account?.balance || 0)}
                </p>
                <button 
                  onClick={() => navigate('/balance-purchase')}
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium mt-2 flex items-center"
                >
                  Top-Up Now <ChevronRight className="w-4 h-4 ml-1" />
                </button>
              </div>
              <div className="p-4 rounded-xl bg-green-500">
                <DollarSign className="h-8 w-8 text-white" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-shadow"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Water Consumed In This Month</p>
                <p className="text-2xl font-bold text-gray-900 mt-2">
                  {(parseInt(stats?.metrics?.usage?.this_month || 0)).toFixed(1)} L
                </p>
                <button 
                  onClick={() => navigate('/analytics')}
                  className="text-sm text-cyan-600 hover:text-cyan-700 font-medium mt-2 flex items-center"
                >
                  View Analytics <ChevronRight className="w-4 h-4 ml-1" />
                </button>
              </div>
              <div className="p-4 rounded-xl bg-cyan-500">
                <Droplets className="h-8 w-8 text-white" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-shadow"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Active Devices</p>
                <p className="text-2xl font-bold text-gray-900 mt-2">
                  {stats?.metrics?.devices?.active || 0}
                </p>
                <button 
                  onClick={() => navigate('/my-devices')}
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium mt-2 flex items-center"
                >
                  Manage Devices <ChevronRight className="w-4 h-4 ml-1" />
                </button>
              </div>
              <div className="p-4 rounded-xl bg-blue-500">
                <Gauge className="h-8 w-8 text-white" />
              </div>
            </div>
          </motion.div>
        </div>

        {/* Alerts Section */}
        <AnimatePresence>
          {alerts.length > 0 && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="space-y-3"
            >
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <AlertCircle className="w-5 h-5 mr-2 text-orange-500" />
                Active Alerts ({unreadAlerts})
              </h3>
              
              {alerts.map((alert, index) => (
                <motion.div
                  key={alert.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ delay: index * 0.1 }}
                  className={`${getAlertColor(alert.severity)} border rounded-xl p-4`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3">
                      <div className="mt-1">
                        {getAlertIcon(alert.alert_type)}
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">{alert.title}</h4>
                        <p className="text-sm text-gray-600 mt-1">{alert.message}</p>
                        <p className="text-xs text-gray-500 mt-2">
                          {new Date(alert.created_at).toLocaleString('id-ID')}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => dismissAlert(alert.id)}
                      className="text-gray-400 hover:text-gray-600 transition"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Water Saving Tips */}
        {tips.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl shadow-sm p-6 border border-green-200"
          >
            <div className="flex items-center space-x-3 mb-4">
              <div className="p-2 bg-green-500 rounded-lg">
                <Award className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Water Saving Tips</h3>
                <p className="text-sm text-gray-600">Save water and reduce your bills</p>
              </div>
            </div>
            
            <div className="space-y-3">
              {tips.map((tip, index) => (
                <motion.div
                  key={tip.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-lg p-4 border border-green-200 hover:shadow-md transition-shadow"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <Zap className="w-4 h-4 text-green-600" />
                        <h4 className="font-semibold text-gray-900">{tip.title}</h4>
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                          Save up to {tip.potential_savings_percentage}%
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">{tip.description}</p>
                    </div>
                    <button
                      onClick={() => markTipViewed(tip.id)}
                      className="ml-4 text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </div>
    </Layout>
  );
};

export default CustomerDashboard;
